import {
  type Conversation as TwilioConversation,
  type Client as TwilioClient,
  type Message as TwilioMessage,
  type Paginator as TwilioMessagesPaginator,
  type MessageUpdateReason as TwilioMessageUpdateReason,
} from '@twilio/conversations';

import { assertNonNullWithReturn } from '../../utils/assert-non-null';

import { type ChatUser, fromChatUserAttributes } from './chat-user';
import { ChatMessageFactory } from './chat-message-factory';
import { type ChatMessagesPaginator } from './chat-message-paginator';
import { chatAttributesSchema } from './chat-attributes';
import { chatUserAttributesSchema } from './chat-user-attributes';
import { type CallbackTeardown, type MessageCreatedCallback, type MessageRemovedCallback, type MessageUpdatedCallback } from './chat-client-listeners';
import { type TextChatMessage } from './text-chat-message';

type BuildAndSendTextMessageParams = Readonly<{

  /** Type. */
  type: 'text';

  /** Body. */
  body: string;
}>;

type BuildAndSendMessageParams =
  | BuildAndSendTextMessageParams;

type ChatConstructorArgs = Pick<
  Chat,
  | 'id'
  | 'me'
  | 'participants'
  | 'unreadMessagesCount'
  | 'latestMessage'
  | 'createdDate'
  | 'updatedDate'
> &
Readonly<{

    /** Twilio chat instance. */
    twilioConversation: TwilioConversation;
  }>;

/** Represents chat instance. */
export class Chat {
  private readonly twilioConversation: TwilioConversation;

  /** ID. */
  public readonly id: number;

  /** Participant object of the current user. */
  public readonly me: ChatUser;

  /**
   * List of participants. Does not include the current user, use `me` for that.
   * For now we suppose there is only one participant in a chat besides the current user.
   */
  public readonly participants: readonly ChatUser[];

  /** Unread messages count. */
  public readonly unreadMessagesCount: number;

  /** Created date of the chat. */
  public readonly createdDate?: Date;

  /** Updated date of the chat. */
  public readonly updatedDate?: Date;

  /** Latest message. */
  public readonly latestMessage?: TextChatMessage;

  private constructor(data: ChatConstructorArgs) {
    this.id = data.id;
    this.me = data.me;
    this.participants = data.participants;
    this.unreadMessagesCount = data.unreadMessagesCount;
    this.latestMessage = data.latestMessage;
    this.createdDate = data.createdDate;
    this.updatedDate = data.updatedDate;
    this.twilioConversation = data.twilioConversation;
  }

  /**
   * Create new chat message.
   * @param data Data for message creation.
   */
  public buildAndSendMessage(data: BuildAndSendMessageParams): Promise<unknown> {
    const { type } = data;

    switch (type) {
      case 'text':
        return this.buildAndSendTextMessage(data.body);
      default:
        // eslint-disable-next-line no-case-declarations
        const _exhaustiveCheck: never = type;
        throw new Error('Invalid message type');
    }
  }

  /** Mark all messages as read. */
  public markAllMessagesAsRead(): void {
    this.twilioConversation.setAllMessagesRead();
  }

  /**
   * A function that determines the order of the chat elements by chat updated date.
   * @param chat1 First chat.
   * @param chat2 Second chat.
   */
  public static compare(chat1: Chat, chat2: Chat): number {
    const chat1LatestMessageDate = chat1.latestMessage?.updatedDate ?? chat1.createdDate;
    const chat2LatestMessageDate = chat2.latestMessage?.updatedDate ?? chat2.createdDate;

    if (chat1LatestMessageDate == null && chat2LatestMessageDate == null) {
      // Indicates that `chat1` and `chat2` are considered equal.
      return 0;
    } else if (chat1LatestMessageDate == null && chat2LatestMessageDate != null) {
      // Indicates that `chat1` should come after `chat2`.
      return 1;
    } else if (chat1LatestMessageDate != null && chat2LatestMessageDate == null) {
      // Indicates that `chat1` should come before `chat2`.
      return -1;
    }

    return assertNonNullWithReturn(chat2LatestMessageDate).getTime() - assertNonNullWithReturn(chat1LatestMessageDate).getTime();
  }

  /**
   * Register callback for `messageAdded` event.
   * @param callback 'messageAdded' event callback.
   */
  public onMessageAdded(callback: MessageCreatedCallback): CallbackTeardown {
    const innerCallback = (twilioMessage: TwilioMessage): void => {
      const message = ChatMessageFactory.createMessage(twilioMessage);
      callback(message);
    };

    this.twilioConversation.addListener('messageAdded', innerCallback);

    return () => {
      this.twilioConversation.removeListener('messageAdded', innerCallback);
    };
  }

  /**
   * Register callback for `messageRemoved` event.
   * @param callback 'messageRemoved' event callback.
   */
  public onMessageRemoved(callback: MessageRemovedCallback): CallbackTeardown {
    const innerCallback = (twilioMessage: TwilioMessage): void => {
      const message = ChatMessageFactory.createMessage(twilioMessage);
      callback(message);
    };

    this.twilioConversation.addListener('messageRemoved', innerCallback);

    return () => {
      this.twilioConversation.removeListener('messageRemoved', innerCallback);
    };
  }

  /**
   * Register callback for `messageUpdated` event.
   * @param callback 'messageUpdated' event callback.
   */
  public onMessageUpdated(callback: MessageUpdatedCallback): CallbackTeardown {
    const innerCallback = ({
      message,
      updateReasons,
    }: Readonly<{
      message: TwilioMessage;
      updateReasons: TwilioMessageUpdateReason[];
    }>): void => {
      const chatMessage = ChatMessageFactory.createMessage(message);
      callback({ message: chatMessage, messageUpdateReasons: updateReasons });
    };

    this.twilioConversation.addListener('messageUpdated', innerCallback);

    return () => {
      this.twilioConversation.removeListener('messageUpdated', innerCallback);
    };
  }

  /**
   * Transform twilio conversation to domain chat model.
   * @param conversation Twilio conversation instance.
   * @param client Twilio client instance.
   */
  public static async fromTwilioConversation(
    conversation: TwilioConversation,
    client: TwilioClient,
  ): Promise<Chat> {
    const rawParticipants = await conversation.getParticipants();
    const users = await Promise.all(rawParticipants.map(p => p.getUser()));

    const mappedUsers = users
      .map(user => {
        try {
          const parsed = chatUserAttributesSchema.parse(user.attributes);
          return fromChatUserAttributes(parsed);
        } catch {
          return null;
        }
      })
      .filter((user): user is ChatUser => user !== null);

    const me = assertNonNullWithReturn(
      mappedUsers.find(
        user => user.id === client.user.identity,
      ),
    );

    const participants = mappedUsers.filter(
      user => user !== me,
    );

    const rawLatestMessage = (
      await conversation.getMessages(1, undefined, 'backwards')
    ).items[0];

    const latestMessage = rawLatestMessage ?
      ChatMessageFactory.createMessage(rawLatestMessage) as TextChatMessage :
      undefined;

    const unreadMessagesCount =
      conversation.lastReadMessageIndex !== null ?
        (await conversation.getUnreadMessagesCount()) ?? 0 :
        await conversation.getMessagesCount();

    const chatAttributes = chatAttributesSchema.parse(conversation.attributes);

    return new Chat({
      me,
      latestMessage,
      unreadMessagesCount,
      id: chatAttributes.chat_id,
      twilioConversation: conversation,
      createdDate: conversation.dateCreated ?? undefined,
      updatedDate: conversation.dateUpdated ?? undefined,
      participants,
    });
  }

  /** Get chat messages paginator. */
  public async getChatMessagesPaginator(): Promise<ChatMessagesPaginator> {
    const paginator = await this.twilioConversation.getMessages();
    return this.fromTwilioMessagesPaginator(paginator);
  }

  private fromTwilioMessagesPaginator(
    paginator: TwilioMessagesPaginator<TwilioMessage>,
  ): ChatMessagesPaginator {
    return {
      hasNextPage: paginator.hasNextPage,
      hasPrevPage: paginator.hasPrevPage,
      prevPage: async () => {
        const twilioPaginator = await paginator.prevPage();
        return this.fromTwilioMessagesPaginator(twilioPaginator);
      },
      nextPage: async () => {
        const twilioPaginator = await paginator.nextPage();
        return this.fromTwilioMessagesPaginator(twilioPaginator);
      },
      items: paginator.items.map(twilioMessage => ChatMessageFactory.createMessage(twilioMessage)),
    };
  }

  private buildAndSendTextMessage(body: string): Promise<unknown> {
    return this.twilioConversation
      .prepareMessage()
      .setBody(body)
      .setAttributes({ type: 'text' })
      .buildAndSend();
  }
}
